import { formatDate } from '@/lib/utils';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '@a7y/tailwind-catalyst';
import type { AdminOrganizationDetail } from '@a7y/types';
import { ArrowUpRight, Building2, Check, Clock, Copy } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Section, UsageStat } from './shared';

export interface OverviewTabProps {
  org: AdminOrganizationDetail['organization'];
  usage: AdminOrganizationDetail['usage'];
  ledger: AdminOrganizationDetail['ledger'];
}

function CopyIdValue({
  value,
  title,
  linkTo,
  linkTitle,
}: {
  value: string;
  title: string;
  linkTo?: string;
  linkTitle?: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">
        {value}
      </code>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
        title={title}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-500" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
      {linkTo && (
        <Link
          to={linkTo}
          className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
          title={linkTitle ?? 'Open detail'}
        >
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}

export function OverviewTab({ org, usage, ledger }: OverviewTabProps) {
  return (
    <div className="space-y-8">
      {/* Organization Info */}
      <Section title="Organization Info" icon={<Building2 className="h-5 w-5" />}>
        <DescriptionList>
          <DescriptionTerm>Name</DescriptionTerm>
          <DescriptionDetails>{org.name}</DescriptionDetails>

          <DescriptionTerm>ID</DescriptionTerm>
          <DescriptionDetails>
            <CopyIdValue value={org.id} title="Copy organization ID" />
          </DescriptionDetails>

          {org.slug && (
            <>
              <DescriptionTerm>Slug</DescriptionTerm>
              <DescriptionDetails>{org.slug}</DescriptionDetails>
            </>
          )}

          {org.timezone && (
            <>
              <DescriptionTerm>Timezone</DescriptionTerm>
              <DescriptionDetails>{org.timezone}</DescriptionDetails>
            </>
          )}

          <DescriptionTerm>Owner ID</DescriptionTerm>
          <DescriptionDetails>
            <CopyIdValue
              value={org.ownerId}
              title="Copy owner ID"
              linkTo={`/admin/users/${org.ownerId}`}
              linkTitle="Open owner detail"
            />
          </DescriptionDetails>

          <DescriptionTerm>Created</DescriptionTerm>
          <DescriptionDetails>{formatDate(org.createdAt)}</DescriptionDetails>

          {org.updatedAt && (
            <>
              <DescriptionTerm>Updated</DescriptionTerm>
              <DescriptionDetails>{formatDate(org.updatedAt)}</DescriptionDetails>
            </>
          )}
        </DescriptionList>
      </Section>

      {/* Usage */}
      {usage && (
        <Section title="Usage" icon={<Clock className="h-5 w-5" />}>
          <div className="grid grid-cols-3 gap-6">
            <UsageStat
              label="Active Projects"
              current={usage.projects}
              limit={ledger?.maxActiveProjects ?? 0}
            />
            <UsageStat label="Target Languages" current={usage.markets} limit={-1} />
            <UsageStat label="Team Members" current={usage.teamMembers} limit={-1} />
          </div>
        </Section>
      )}
    </div>
  );
}
